<script setup lang="ts">
import { computed, onMounted, PropType, ref } from 'vue';
import { useRouter } from 'vue-router';
import { nextTick } from 'vue';
import { Trip } from "@/models/Trip";
import { SavedOffer } from "@/models/SavedOffer";
import TripDeleteModal from "@/components/TripCard/TripDeleteModal.vue";
import TripEditModal from "@/components/TripCard/TripEditModal.vue";
import { useI18n } from "vue-i18n";
import { useTripsStore } from "@/stores/tripsStore";
import boat1 from "@/assets/about/boat-1.png";
import {useFormatStore} from "@/stores/formatStore";

const props = defineProps({
  trip: {
    type: Object as PropType<Trip>,
    required: true
  },
});

const tripsStore = useTripsStore();
const formatStore = useFormatStore();
const { t } = useI18n();
const tripToDelete = ref(null);
const showDeleteModal = ref(false);
const showEditModal = ref(false);
const offer = ref<SavedOffer[]>([]);


const colors = ['#faffd8', '#fff3eb', '#eafeec', '#efebff'];
const color = colors[Math.floor(Math.random() * colors.length)];


const defaultImage = boat1;
const backgroundImageClass = ref({
  backgroundImage: `url('${props.trip.backgroundImageUrl || defaultImage}')`
});


const tripStatus = computed(() => props.trip.state);

const statusStyle = computed(() => {
  const statusColors = {
    open: '#f9f9f9',
    offer: '#9be7b9',
    active: '#9be7b9',
    done: '#9be7b9',
    cancelled: '#ff9d9d',
    planned: '#f9f9f9',
    unknown: 'black'
  };
  return { background: statusColors[tripStatus.value] };
});

// Ensure `statusText` uses the mapped `tripStatus`
const statusText = computed(() => {
  const statusLabels = {
    open: t('tripCard.open'),
    offer: t('tripCard.offer'),
    active: t('tripCard.confirmed'),
    done: t('tripCard.completed'),
    cancelled: t('tripCard.cancelled'),
    planned: t('tripCard.planned'),
    unknown: t('tripCard.unknown')
  };
  return statusLabels[tripStatus.value];
});

const isDeletingTrip = ref(false);
const onDelete = async (uuid: string) => {
  try {
    isDeletingTrip.value = true;
    await tripsStore.removeTrip(uuid);
    console.log("THIS IS THE UUID",uuid);
    showDeleteModal.value = false;
    tripToDelete.value = null;
    await tripsStore.getAll();
  } catch (error) {
    console.error(`Failed to delete trip ${uuid}:`, error);
  }finally {
    isDeletingTrip.value = false;
  }
};


const onEdit = async (data: { uuid: string, name: string }) => {
  try {
    await tripsStore.updateTrip(data.uuid, { name: data.name });
    showEditModal.value = false;
    await tripsStore.getAll();

    const updatedTrip = tripsStore.trips.find(t => t.uuid === data.uuid);

    if (updatedTrip) {
      backgroundImageClass.value = {
        backgroundImage: `url('${updatedTrip.backgroundImageUrl || defaultImage}')`
      };
    } else {
      console.warn('Trip not found after update:', data.uuid);
    }
  } catch (error) {
    console.error(`Failed to update trip ${data.uuid}:`, error);
  }
};

function updateTripImage(newUrl: string) {
  const updatedTrip = { ...props.trip, backgroundImageUrl: newUrl };
  Object.assign(props.trip, updatedTrip);
}

const router = useRouter();
const handleTripClick = async () => {
  await tripsStore.loadTrip(props.trip.uuid);
  await nextTick(); // ensure store is updated before navigating
  router.push(`/my/trip/${props.trip.uuid}`);
};

</script>

<template>
  <div
      v-if="offer !== null"
      class="flex flex-col rounded-11xl" 
      :style="{ background: color }"
      :class="tripStatus === 'cancelled' || tripStatus === 'lost' ? 'cursor-not-allowed pointer-events-none' : ''"
  >

    <!-- Greyed out when trip status is cancelled or lost -->
    <div
        v-if="tripStatus === 'cancelled' || tripStatus === 'lost'"
        class="absolute inset-0 bg-white/70 rounded-11xl z-10"
    ></div>
    <div :style="backgroundImageClass"
      class="flex flex-col justify-space-between bg-cover bg-no-repeat h-56 rounded-11xl cursor-pointer p-5"
      @click="handleTripClick">

      <div class="responsive-body-xsmall rounded-11xl flex px-3 py-1 w-fit flex-row gap-1.5 justify-center relative z-20" :style="statusStyle">
        <img v-if="tripStatus === 'open' || tripStatus === 'offer' || tripStatus === 'active' || tripStatus === 'planned'" src="@/assets/icons/svg/Confirmed_16x16.svg" alt="" width="16" height="16" />
        <img v-else-if="tripStatus === 'done'" src="@/assets/icons/svg/Completed_16x16.svg" alt="" width="16" height="16" />
        <img v-else-if="tripStatus === 'cancelled' || tripStatus === 'lost'" src="@/assets/icons/svg/Cancelled_16x16.svg" alt="" width="16" height="16" />
        <span style="line-height:17px">{{ statusText }}</span>
      </div>
      <div class="responsive-title-4 font-semibold text-white trip-name">{{ trip.name }}
        <div class="flex responsive-body-small saved-offer__text gap-x-1.5 text-white">
        <span class="font-semibold">{{ props.trip.offers.length }}</span> <span class="font-normal">{{ t('tripCard.savedOffers') }}</span>
        </div>
      </div>
      
    </div>
    <div class="flex flex-row p-4 justify-between items-center">
      <div>
        <div class="responsive-body-small font-semibold flex items-center gap-1">
          <img src="@/assets/icons/svg/Location_16x16.svg" alt="location" class="w-3 h-3" />
          {{ trip.locationId?.name }}
        </div>
        <div class="responsive-body-xsmall text-gray-600 flex items-center gap-1">
          <img src="@/assets/icons/svg/Calendar_16x16.svg" alt="calendar" class="w-3 h-3" />
          <div class="mt-0.5">
            {{
              formatStore.formatDateWithCustomPatternMappingLocalized(
                  trip.dateStart,
                  'MMM DD, YYYY'
              )
            }}
            -
            {{
              formatStore.formatDateWithCustomPatternMappingLocalized(
                  trip.dateEnd,
                  'MMM DD, YYYY'
              )
            }}
          </div>
        </div>
      </div>
      <div>
        <ElDropdown trigger="click"
                    placement="left"
                    teleported="false"
                    :popper-options="{
                        modifiers: [
                          {
                            name: 'preventOverflow',
                            options: {
                              boundary: 'viewport',
                              padding: 20
                            }
                          },
                          {
                            name: 'offset',
                            options: {
                              offset: [0, 10]
                            }
                          }
                        ]
                      }">
          <img src="@/assets/icons/svg/Menu dots_24x24.svg">
          <template #dropdown>
            <ElDropdownMenu>
              <ElDropdownItem class="text-black flex gap-2" @click="showEditModal = true">
                <img src="@/assets/icons/svg/Edit_24x24.svg" />
                {{ t('tripCard.renameTrip') }}
              </ElDropdownItem>
              <ElDropdownItem class="text-black flex gap-2" @click="showDeleteModal = true">
                <img src="@/assets/icons/svg/Delete_24x24.svg" />
                {{ t('tripCard.deleteTrip') }}
              </ElDropdownItem>
            </ElDropdownMenu>
          </template>
        </ElDropdown>
      </div>
    </div>
  </div>
  <TripDeleteModal 
      :showDeleteModal="showDeleteModal"
      @on-close="showDeleteModal = false"
      :trip="trip"
      :isDeleting="isDeletingTrip"
      @on-delete-click="onDelete(trip.uuid)"/>
  <TripEditModal
      v-if="trip"
      :show="showEditModal"
      :name="trip.name"
      :uuid="trip.uuid"
      :backgroundImageUrl="trip.backgroundImageUrl"
      @on-close="showEditModal = false"
      @on-edit-click="(newName) => onEdit({ uuid: trip.uuid, name: newName })"
  />
</template>

<style scoped>
@media (max-width: 1024px) {
  .trip-name {
    font-size: 1.25rem;
  }

  .saved-offer__text {
    font-size: .875rem;
  }
}

</style>
